import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  LinearProgress,
  Sheet,
  Stack,
  Typography,
  Avatar,
  IconButton,
} from "@mui/joy";
import { useState } from "react";

// ─── Icons (MUI Material) ─────────────────────────────────────────────────────
import AssignmentRoundedIcon       from "@mui/icons-material/AssignmentRounded";
import CheckCircleRoundedIcon      from "@mui/icons-material/CheckCircleRounded";
import HourglassEmptyRoundedIcon   from "@mui/icons-material/HourglassEmptyRounded";
import WarningAmberRoundedIcon     from "@mui/icons-material/WarningAmberRounded";
import AccessTimeRoundedIcon       from "@mui/icons-material/AccessTimeRounded";
import CalendarMonthRoundedIcon    from "@mui/icons-material/CalendarMonthRounded";
import PersonRoundedIcon           from "@mui/icons-material/PersonRounded";
import OpenInNewRoundedIcon        from "@mui/icons-material/OpenInNewRounded";
import LabelRoundedIcon            from "@mui/icons-material/LabelRounded";
import FlagRoundedIcon             from "@mui/icons-material/FlagRounded";
import ChatBubbleRoundedIcon       from "@mui/icons-material/ChatBubbleRounded";
import TrendingUpRoundedIcon       from "@mui/icons-material/TrendingUpRounded";
import PendingActionsRoundedIcon   from "@mui/icons-material/PendingActionsRounded";
import CancelRoundedIcon           from "@mui/icons-material/CancelRounded";
import PlayCircleRoundedIcon       from "@mui/icons-material/PlayCircleRounded";
import PauseCircleRoundedIcon      from "@mui/icons-material/PauseCircleRounded";
import ViewKanbanRoundedIcon       from "@mui/icons-material/ViewKanbanRounded";

// ─── Константы ────────────────────────────────────────────────────────────────
const MONTHS_SHORT = ["янв","фев","мар","апр","май","июн","июл","авг","сен","окт","ноя","дек"];

const STATUS_CONFIG = {
  new:         { label: "Новая",        color: "#6366f1", bg: "rgba(99,102,241,0.1)",   icon: <PendingActionsRoundedIcon sx={{ fontSize: 12 }} /> },
  in_progress: { label: "В работе",     color: "#f59e0b", bg: "rgba(245,158,11,0.1)",   icon: <PlayCircleRoundedIcon sx={{ fontSize: 12 }} /> },
  review:      { label: "На проверке",  color: "#0ea5e9", bg: "rgba(14,165,233,0.1)",   icon: <HourglassEmptyRoundedIcon sx={{ fontSize: 12 }} /> },
  done:        { label: "Завершена",    color: "#10b981", bg: "rgba(16,185,129,0.1)",   icon: <CheckCircleRoundedIcon sx={{ fontSize: 12 }} /> },
  blocked:     { label: "Заблокирована",color: "#ef4444", bg: "rgba(239,68,68,0.1)",    icon: <CancelRoundedIcon sx={{ fontSize: 12 }} /> },
  paused:      { label: "На паузе",     color: "#8b5cf6", bg: "rgba(139,92,246,0.1)",   icon: <PauseCircleRoundedIcon sx={{ fontSize: 12 }} /> },
};

const PRIORITY_CONFIG = {
  low:      { label: "Низкий",    color: "#10b981" },
  medium:   { label: "Средний",   color: "#f59e0b" },
  high:     { label: "Высокий",   color: "#ef4444" },
  critical: { label: "Критичный", color: "#dc2626" },
};

// ─── Тестовые данные ──────────────────────────────────────────────────────────
const MOCK_STATS = {
  total: 24,
  done: 9,
  in_progress: 8,
  overdue: 3,
  review: 4,
  new: 3,
};

const MOCK_UPCOMING = [
  {
    id: 101,
    title: "Разработка API авторизации",
    project: "CRM Backend",
    assignee: { name: "Алексей Петров", avatar: "АП" },
    due_date: "2026-03-15",
    status: "in_progress",
    priority: "high",
    tags: ["backend", "auth"],
    comments: 5,
  },
  {
    id: 102,
    title: "Дизайн страницы онбординга",
    project: "Mobile App",
    assignee: { name: "Мария Соколова", avatar: "МС" },
    due_date: "2026-03-18",
    status: "new",
    priority: "medium",
    tags: ["design", "ux"],
    comments: 2,
  },
  {
    id: 103,
    title: "Написать unit-тесты для модуля оплаты",
    project: "CRM Backend",
    assignee: { name: "Дмитрий Иванов", avatar: "ДИ" },
    due_date: "2026-03-20",
    status: "new",
    priority: "medium",
    tags: ["testing"],
    comments: 0,
  },
];

const MOCK_ACTIVE = [
  {
    id: 201,
    title: "Рефакторинг компонента таблицы",
    project: "Admin Panel",
    assignee: { name: "Кирилл Разраб", avatar: "КР" },
    due_date: "2026-03-05",
    status: "in_progress",
    priority: "high",
    tags: ["frontend", "refactoring"],
    comments: 8,
    overdue: true,
  },
  {
    id: 202,
    title: "Настройка CI/CD пайплайна",
    project: "Infrastructure",
    assignee: { name: "Олег Сервер", avatar: "ОС" },
    due_date: "2026-03-10",
    status: "review",
    priority: "critical",
    tags: ["devops"],
    comments: 3,
    overdue: false,
  },
  {
    id: 203,
    title: "Интеграция с платёжной системой",
    project: "CRM Backend",
    assignee: { name: "Алексей Петров", avatar: "АП" },
    due_date: "2026-03-03",
    status: "blocked",
    priority: "critical",
    tags: ["backend", "payments"],
    comments: 12,
    overdue: true,
  },
];

// ─── Утилиты ──────────────────────────────────────────────────────────────────
function parseDateParts(dateStr) {
  if (!dateStr) return { day: "—", monthIdx: 0 };
  const parts = dateStr.includes("-") ? dateStr.split("-") : dateStr.split(".");
  if (dateStr.includes("-")) {
    return { day: parts[2], monthIdx: Number(parts[1]) - 1 };
  }
  return { day: parts[0], monthIdx: Number(parts[1]) - 1 };
}

// ─── StatCard ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, sublabel, color, icon, progress }) {
  return (
    <Card
      variant="outlined"
      sx={{
        flex: 1,
        minWidth: { xs: "calc(50% - 6px)", sm: 150 },
        border: "1.5px solid",
        borderColor: "divider",
        borderRadius: "16px",
        bgcolor: "background.surface",
        boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
        transition: "box-shadow 0.2s, transform 0.2s",
        "&:hover": { boxShadow: "0 6px 20px rgba(0,0,0,0.09)", transform: "translateY(-1px)" },
        p: 0,
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography
              sx={{
                fontSize: "2.4rem", fontWeight: 800, lineHeight: 1,
                color: color || "text.primary",
                fontFamily: "'Sora', sans-serif", letterSpacing: "-1.5px",
              }}
            >
              {value}
            </Typography>
            <Typography level="body-xs" sx={{ mt: 0.5, color: "text.secondary", fontWeight: 600 }}>{label}</Typography>
            {sublabel && <Typography level="body-xs" sx={{ color: "text.tertiary", mt: 0.2, fontSize: "0.68rem" }}>{sublabel}</Typography>}
          </Box>
          <Box sx={{
            width: 40, height: 40, borderRadius: "12px",
            bgcolor: color ? `${color}15` : "neutral.softBg",
            display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.15rem",
          }}>
            {icon}
          </Box>
        </Stack>
        {progress !== undefined && (
          <LinearProgress
            value={progress} determinate
            sx={{ mt: 1.5, borderRadius: 8, height: 4, "--LinearProgress-progressColor": color || "#6366f1", "--LinearProgress-trackColor": "#f1f5f9" }}
          />
        )}
      </CardContent>
    </Card>
  );
}

// ─── SectionHeader ────────────────────────────────────────────────────────────
function SectionHeader({ title, count, action }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Typography level="title-md" sx={{ fontWeight: 700, fontFamily: "'Sora', sans-serif", color: "text.primary", letterSpacing: "-0.2px" }}>
          {title}
        </Typography>
        {count !== undefined && (
          <Box sx={{ px: 1, py: 0.25, borderRadius: "20px", bgcolor: "#6366f112", border: "1px solid #6366f120" }}>
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 800, color: "#6366f1" }}>{count}</Typography>
          </Box>
        )}
      </Stack>
      {action && (
        <Typography
          level="body-xs"
          sx={{ color: "#6366f1", fontWeight: 600, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
        >
          {action}
        </Typography>
      )}
    </Stack>
  );
}

// ─── TaskCardCompact (Вид 1 — строчная карточка, как UpcomingRow) ─────────────
export function TaskCardCompact({ task, onOpen }) {
  const { day, monthIdx } = parseDateParts(task?.due_date);
  const status = STATUS_CONFIG[task?.status] || STATUS_CONFIG["new"];
  const priority = PRIORITY_CONFIG[task?.priority] || PRIORITY_CONFIG["medium"];

  return (
    <Box sx={{
      borderRadius: "16px",
      border: "1.5px solid rgba(99,102,241,0.12)",
      background: "white",
      boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
      overflow: "hidden",
      transition: "box-shadow 0.18s, transform 0.18s",
      "&:hover": { boxShadow: "0 6px 24px rgba(99,102,241,0.13)", transform: "translateY(-1px)" },
    }}>
      {/* Верхняя полоска */}
      <Box sx={{ height: 3, background: "linear-gradient(90deg, #6366f1, #818cf8)" }} />

      <Box sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          {/* Дата-плашка */}
          <Box sx={{
            minWidth: 52, height: 52, borderRadius: "13px",
            background: "rgba(99,102,241,0.08)",
            border: "1.5px solid rgba(99,102,241,0.18)",
            display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 800, color: "#6366f1", lineHeight: 1, fontFamily: "'Sora', sans-serif" }}>
              {day}
            </Typography>
            <Typography sx={{ fontSize: "0.6rem", color: "#6366f1", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", opacity: 0.8 }}>
              {MONTHS_SHORT[monthIdx]}
            </Typography>
          </Box>

          {/* Контент */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography level="body-sm" sx={{ fontWeight: 700, fontFamily: "'Sora', sans-serif", color: "#1a1a2e" }} noWrap>
                #{task?.id} · {task?.project}
              </Typography>
              <Chip
                size="sm"
                startDecorator={status.icon}
                sx={{ background: status.bg, color: status.color, fontWeight: 700, border: "none", fontSize: "0.66rem", flexShrink: 0, ml: 1 }}
              >
                {status.label}
              </Chip>
            </Stack>

            <Typography level="body-xs" sx={{ color: "#1a1a2e", fontWeight: 600, mb: 0.5, lineHeight: 1.4 }} noWrap>
              {task?.title}
            </Typography>

            <Stack direction="row" flexWrap="wrap" gap={1.5} sx={{ mb: 1 }}>
              {task?.assignee && (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <PersonRoundedIcon sx={{ fontSize: 12, color: "text.tertiary" }} />
                  <Typography level="body-xs" sx={{ color: "text.secondary" }}>{task.assignee.name}</Typography>
                </Stack>
              )}
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <FlagRoundedIcon sx={{ fontSize: 12, color: priority.color }} />
                <Typography level="body-xs" sx={{ color: priority.color, fontWeight: 600 }}>{priority.label}</Typography>
              </Stack>
              {task?.comments > 0 && (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <ChatBubbleRoundedIcon sx={{ fontSize: 12, color: "text.tertiary" }} />
                  <Typography level="body-xs" sx={{ color: "text.secondary" }}>{task.comments}</Typography>
                </Stack>
              )}
            </Stack>

            <Button
              size="sm" variant="soft" fullWidth
              endDecorator={<OpenInNewRoundedIcon sx={{ fontSize: 14 }} />}
              onClick={() => onOpen && onOpen(task)}
              sx={{
                borderRadius: "10px", fontWeight: 700, fontSize: "0.75rem",
                background: "rgba(99,102,241,0.08)", color: "#6366f1",
                "&:hover": { background: "rgba(99,102,241,0.15)" },
              }}
            >
              Открыть задачу
            </Button>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}

// ─── TaskCardFull (Вид 2 — карточка с кнопками действий, как на скрине 2) ─────
export function TaskCardFull({ task, onOpen, onComplete, onFail }) {
  const { day, monthIdx } = parseDateParts(task?.due_date);
  const status = STATUS_CONFIG[task?.status] || STATUS_CONFIG["new"];
  const priority = PRIORITY_CONFIG[task?.priority] || PRIORITY_CONFIG["medium"];
  const accentColor = task?.overdue ? "#ef4444" : "#6366f1";
  const accentBg = task?.overdue ? "rgba(239,68,68,0.08)" : "rgba(99,102,241,0.08)";

  return (
    <Box sx={{
      borderRadius: "16px",
      border: `1.5px solid ${task?.overdue ? "rgba(239,68,68,0.2)" : "rgba(99,102,241,0.15)"}`,
      background: task?.overdue ? "rgba(239,68,68,0.02)" : "white",
      boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
      overflow: "hidden",
      transition: "box-shadow 0.18s, transform 0.18s",
      "&:hover": { boxShadow: `0 6px 24px ${accentColor}20`, transform: "translateY(-1px)" },
    }}>
      {/* Верхняя полоска */}
      <Box sx={{
        height: 3,
        background: task?.overdue
          ? "linear-gradient(90deg, #ef4444, #f87171)"
          : "linear-gradient(90deg, #6366f1, #818cf8)",
      }} />

      <Box sx={{ p: 2 }}>
        {/* Заголовок */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography level="body-sm" sx={{ fontWeight: 700, fontFamily: "'Sora', sans-serif", color: "#6366f1" }}>
            #{task?.id} · <Typography component="span" sx={{ color: "#1a1a2e", fontWeight: 700 }}>№{task?.project}</Typography>
          </Typography>
          <Stack direction="row" spacing={0.6}>
            {task?.overdue && (
              <Chip size="sm" sx={{ background: "rgba(239,68,68,0.1)", color: "#ef4444", fontWeight: 700, border: "none", fontSize: "0.64rem" }}
                startDecorator={<WarningAmberRoundedIcon sx={{ fontSize: 11 }} />}
              >
                Просрочена
              </Chip>
            )}
            <Chip
              size="sm"
              startDecorator={status.icon}
              sx={{ background: status.bg, color: status.color, fontWeight: 700, border: "none", fontSize: "0.66rem" }}
            >
              {status.label}
            </Chip>
          </Stack>
        </Stack>

        {/* Название задачи */}
        <Typography level="body-sm" sx={{ fontWeight: 600, color: "#1a1a2e", mb: 1, lineHeight: 1.4 }}>
          {task?.title}
        </Typography>

        {/* Метаданные */}
        <Stack spacing={0.6} sx={{ mb: 1.5 }}>
          <Stack direction="row" alignItems="center" spacing={0.7}>
            <PersonRoundedIcon sx={{ fontSize: 13, color: accentColor }} />
            <Typography level="body-xs" sx={{ color: "#1a1a2e", fontWeight: 500 }}>{task?.assignee?.name}</Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.7}>
            <CalendarMonthRoundedIcon sx={{ fontSize: 13, color: "text.tertiary" }} />
            <Typography level="body-xs" sx={{ color: "text.secondary" }}>
              {day} {MONTHS_SHORT[monthIdx]} 2026 г.
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.7}>
            <FlagRoundedIcon sx={{ fontSize: 13, color: priority.color }} />
            <Typography level="body-xs" sx={{ color: priority.color, fontWeight: 600 }}>
              {priority.label} приоритет
            </Typography>
          </Stack>
          {task?.tags?.length > 0 && (
            <Stack direction="row" alignItems="center" spacing={0.7} flexWrap="wrap">
              <LabelRoundedIcon sx={{ fontSize: 13, color: "text.tertiary" }} />
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {task.tags.map(tag => (
                  <Typography key={tag} level="body-xs" sx={{
                    px: 0.8, py: 0.15, borderRadius: "6px",
                    bgcolor: "rgba(99,102,241,0.07)", color: "#6366f1",
                    fontWeight: 600, fontSize: "0.65rem",
                  }}>
                    #{tag}
                  </Typography>
                ))}
              </Stack>
            </Stack>
          )}
        </Stack>

        {/* Кнопки действий */}
        <Stack spacing={0.8}>
          <Stack direction="row" spacing={0.8}>
            <Button
              size="sm"
              onClick={() => onComplete && onComplete(task)}
              startDecorator={<CheckCircleRoundedIcon sx={{ fontSize: 15 }} />}
              sx={{
                flex: 1, borderRadius: "10px", fontWeight: 700, fontSize: "0.72rem",
                background: "linear-gradient(135deg, #10b981, #34d399)",
                color: "white",
                "&:hover": { transform: "scale(1.01)", background: "linear-gradient(135deg, #059669, #10b981)" },
                transition: "all 0.15s",
              }}
            >
              Завершить
            </Button>
            <Button
              size="sm" variant="outlined"
              onClick={() => onFail && onFail(task)}
              startDecorator={<CancelRoundedIcon sx={{ fontSize: 15 }} />}
              sx={{
                flex: 0.6, borderRadius: "10px", fontWeight: 700, fontSize: "0.72rem",
                borderColor: "rgba(239,68,68,0.4)", color: "#ef4444",
                "&:hover": { background: "rgba(239,68,68,0.06)", borderColor: "#ef4444" },
              }}
            >
              Неудачная
            </Button>
          </Stack>
          <Stack direction="row" spacing={0.8}>
            <Button
              size="sm" variant="outlined"
              onClick={() => onOpen && onOpen(task)}
              startDecorator={<OpenInNewRoundedIcon sx={{ fontSize: 15 }} />}
              sx={{
                flex: 1, borderRadius: "10px", fontWeight: 700, fontSize: "0.72rem",
                borderColor: "divider", color: "text.secondary",
                "&:hover": { background: "rgba(0,0,0,0.03)", borderColor: "neutral.300" },
              }}
            >
              Открыть
            </Button>
            <Button
              size="sm" variant="outlined"
              startDecorator={<ChatBubbleRoundedIcon sx={{ fontSize: 15 }} />}
              sx={{
                flex: 0.6, borderRadius: "10px", fontWeight: 700, fontSize: "0.72rem",
                borderColor: "divider", color: "text.secondary",
                "&:hover": { background: "rgba(0,0,0,0.03)" },
              }}
            >
              Чат {task?.comments > 0 && `(${task.comments})`}
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}

// ─── Главная страница дашборда ─────────────────────────────────────────────────
export default function KanbanDashboard() {
  const today = new Date().toLocaleDateString("ru-RU", {
    weekday: "long", day: "numeric", month: "long",
  });

  const completionPct = Math.round((MOCK_STATS.done / MOCK_STATS.total) * 100);

  return (
    <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2, pb: 6, maxWidth: 2100, mx: "auto" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&display=swap');`}</style>

      {/* ── Приветствие ── */}
      <Box sx={{ mb: 4 }}>
        <Typography sx={{
          fontSize: "clamp(1.4rem, 3vw, 2rem)", fontWeight: 800,
          fontFamily: "'Sora', sans-serif", letterSpacing: "-0.8px",
          lineHeight: 1.2, color: "text.primary",
        }}>
          Добро пожаловать, Кирилл Разраб (Разработчик)! 👋
        </Typography>
        <Typography level="body-sm" sx={{ color: "text.secondary", mt: 0.5, textTransform: "capitalize", fontWeight: 500 }}>
          {today}
        </Typography>
      </Box>

      {/* ── Прогресс месяца ── */}
      <Card
        variant="outlined"
        sx={{
          mb: 3, borderRadius: "20px", border: "1.5px solid #e0e7ff",
          background: "linear-gradient(135deg, #f8faff 0%, #eef2ff 100%)",
          boxShadow: "0 2px 16px rgba(99,102,241,0.08)", p: 0,
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={2.5}>
            <Box>
              <Typography sx={{ fontSize: "0.68rem", fontWeight: 800, color: "#6366f1", letterSpacing: "0.1em", textTransform: "uppercase", mb: 0.75 }}>
                Прогресс за месяц
              </Typography>
              <Typography sx={{ fontSize: "3.5rem", fontWeight: 800, fontFamily: "'Sora', sans-serif", letterSpacing: "-2px", lineHeight: 1, color: "#6366f1" }}>
                {completionPct}
                <Typography component="span" sx={{ fontSize: "1.5rem", fontWeight: 600, color: "#6366f1", opacity: 0.7 }}>%</Typography>
              </Typography>
              <Typography level="body-xs" sx={{ color: "text.secondary", mt: 0.5, fontWeight: 500 }}>
                Выполнено {MOCK_STATS.done} из {MOCK_STATS.total} задач
              </Typography>
            </Box>
            <Box sx={{ width: { xs: "100%", sm: 300 } }}>
              <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.75 }}>
                <Typography level="body-xs" sx={{ color: "text.secondary", fontWeight: 500 }}>Прогресс выполнения</Typography>
                <Typography level="body-xs" sx={{ fontWeight: 800, color: "#6366f1", fontFamily: "'Sora', sans-serif" }}>
                  {MOCK_STATS.done}/{MOCK_STATS.total}
                </Typography>
              </Stack>
              <LinearProgress
                value={completionPct} determinate
                sx={{ borderRadius: 8, height: 10, "--LinearProgress-progressColor": "#6366f1", "--LinearProgress-trackColor": "#c7d2fe" }}
              />
              <Stack direction="row" justifyContent="space-between" sx={{ mt: 1.25 }}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#f59e0b" }} />
                  <Typography level="body-xs" sx={{ color: "text.secondary" }}>В работе: <strong>{MOCK_STATS.in_progress}</strong></Typography>
                </Stack>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#0ea5e9" }} />
                  <Typography level="body-xs" sx={{ color: "text.secondary" }}>На проверке: <strong>{MOCK_STATS.review}</strong></Typography>
                </Stack>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#ef4444" }} />
                  <Typography level="body-xs" sx={{ color: "text.secondary" }}>Просрочено: <strong>{MOCK_STATS.overdue}</strong></Typography>
                </Stack>
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* ── Мини-статистика ── */}
      <Stack direction="row" flexWrap="wrap" gap={1.5} sx={{ mb: 4 }} useFlexGap>
        <StatCard label="Всего задач"     value={MOCK_STATS.total}       sublabel="за месяц"          icon="📋" color="#6366f1" progress={completionPct} />
        <StatCard label="Завершено"        value={MOCK_STATS.done}        sublabel={`${completionPct}% от плана`} icon="✅" color="#10b981" />
        <StatCard label="В работе"         value={MOCK_STATS.in_progress} sublabel="активных задач"   icon="🔄" color="#f59e0b" />
        <StatCard label="Просрочено"       value={MOCK_STATS.overdue}     sublabel="требуют внимания" icon="⚠️" color="#ef4444" />
        <StatCard label="На проверке"      value={MOCK_STATS.review}      sublabel="ожидают ревью"    icon="🔍" color="#0ea5e9" />
        <StatCard label="Новые"            value={MOCK_STATS.new}         sublabel="не взяты в работу" icon="🆕" color="#8b5cf6" />
      </Stack>

      {/* ── Демо карточек ── */}
      <Box sx={{ mb: 4, p: 2.5, borderRadius: "16px", border: "1.5px dashed rgba(99,102,241,0.2)", background: "rgba(99,102,241,0.02)" }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <ViewKanbanRoundedIcon sx={{ color: "#6366f1", fontSize: 18 }} />
          <Typography level="title-sm" sx={{ fontWeight: 700, color: "#6366f1", fontFamily: "'Sora', sans-serif" }}>
            Два вида карточек задач
          </Typography>
        </Stack>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="flex-start">
          <Box sx={{ flex: 1 }}>
            <Typography level="body-xs" sx={{ color: "text.tertiary", mb: 1, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Вид 1 — Компактная (для списков / канбана)
            </Typography>
            <TaskCardCompact task={MOCK_UPCOMING[0]} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography level="body-xs" sx={{ color: "text.tertiary", mb: 1, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Вид 2 — Полная (с кнопками действий)
            </Typography>
            <TaskCardFull task={MOCK_ACTIVE[0]} />
          </Box>
        </Stack>
      </Box>

      {/* ── Две колонки ── */}
      <Stack direction={{ xs: "column", md: "row" }} spacing={3} alignItems="flex-start">

        {/* Предстоящие задачи — Вид 1 */}
        <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
          <SectionHeader title="Предстоящие задачи" count={MOCK_UPCOMING.length} action="Все задачи →" />
          <Stack spacing={1.5}>
            {MOCK_UPCOMING.map(task => (
              <TaskCardCompact key={task.id} task={task} onOpen={(t) => console.log("open", t)} />
            ))}
          </Stack>
        </Box>

        <Divider orientation="vertical" sx={{ display: { xs: "none", md: "flex" } }} />

        {/* Активные задачи — Вид 2 */}
        <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
          <SectionHeader title="Активные задачи" count={MOCK_ACTIVE.length} action="Все задачи →" />
          <Stack spacing={1.5}>
            {MOCK_ACTIVE.map(task => (
              <TaskCardFull
                key={task.id}
                task={task}
                onOpen={(t) => console.log("open", t)}
                onComplete={(t) => alert(`Задача #${t.id} завершена!`)}
                onFail={(t) => alert(`Задача #${t.id} отмечена как неудачная`)}
              />
            ))}
          </Stack>
        </Box>

      </Stack>
    </Box>
  );
}